import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../../components/layout/AdminShell';
import { getAdminNavigation } from '../../constants/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/supabase';
import Toast, { ToastType } from '../../components/ui/Toast';
import { ProspectTitle } from '../../types';
import {
  ArrowLeftIcon,
  UserPlusIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  CreditCardIcon,
  ComputerDesktopIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

const RecruitForm: React.FC = () => {
  const navigate = useNavigate();
  const { loanOfficer: currentUser, isVA } = useAuth();
  const navigation = getAdminNavigation(isVA);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    title: 'Sr Mortgage Broker' as ProspectTitle,
    nmls: '',
    sponsor_date: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    // Validate required fields
    if (!formData.first_name || !formData.last_name || !formData.phone || !formData.email || !formData.nmls || !formData.sponsor_date) {
      setToast({ message: 'Please fill in all required fields', type: 'error' });
      return;
    }

    try {
      setSubmitting(true);

      // Create the recruit via API
      await api.createRecruit({
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        email: formData.email,
        title: formData.title,
        nmls: formData.nmls,
        expected_start_date: formData.sponsor_date,
        recruiter_id: currentUser.id,
        notes: formData.notes.trim() || undefined,
      });

      setToast({ message: 'Recruit added successfully! Expectations email sent.', type: 'success' });

      // Navigate back to recruits tab after short delay
      setTimeout(() => {
        navigate('/admin/prospects?tab=recruits');
      }, 1500);
    } catch (error: any) {
      setToast({ message: error.message || 'Failed to create recruit', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminShell
      title="Add Recruit"
      description="Add a new recruit to the onboarding pipeline"
      nav={navigation}
    >
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="space-y-6">
        {/* Back button */}
        <button
          onClick={() => navigate('/admin/prospects')}
          className="inline-flex items-center gap-2 text-sm font-semibold text-lumin-primary hover:text-[#014f70]"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Pipeline
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form Section - 2/3 width */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-2xl bg-amber-100 flex items-center justify-center">
                  <UserPlusIcon className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Recruit Information</h2>
                  <p className="text-sm text-slate-500">Enter the recruit's basic details</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lumin-primary"
                    placeholder="John"
                    required
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lumin-primary"
                    placeholder="Doe"
                    required
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lumin-primary"
                    placeholder="(555) 123-4567"
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Personal Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lumin-primary"
                    placeholder="john@personal.com"
                    required
                  />
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lumin-primary bg-white"
                    required
                  >
                    <option value="Sr Mortgage Broker">Sr Mortgage Broker</option>
                    <option value="Executive Branch Manager">Executive Branch Manager</option>
                  </select>
                </div>

                {/* NMLS */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    NMLS Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="nmls"
                    value={formData.nmls}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lumin-primary"
                    placeholder="123456"
                    required
                  />
                </div>

                {/* Sponsor Date */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Sponsor Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="sponsor_date"
                    value={formData.sponsor_date}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lumin-primary"
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">Expected start date</p>
                </div>

                {/* Notes */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Notes (internal)
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                    placeholder="Add notes for VAs and admins..."
                    rows={3}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lumin-primary resize-none"
                  />
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-slate-200">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-6 py-4 rounded-2xl text-base font-semibold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Adding Recruit...
                    </>
                  ) : (
                    <>
                      <UserPlusIcon className="h-5 w-5" />
                      Add Recruit & Send Expectations Email
                    </>
                  )}
                </button>
                <p className="text-xs text-slate-500 text-center mt-3">
                  An expectations email will be sent immediately to the recruit (CC: you)
                </p>
              </div>
            </form>
          </div>

          {/* Cheat Sheet Sidebar - 1/3 width */}
          <div className="lg:col-span-1">
            <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm sticky top-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <ChatBubbleLeftRightIcon className="h-5 w-5 text-lumin-primary" />
                Recruiter Cheat Sheet
              </h3>
              <p className="text-xs text-slate-500 mb-4">Key points to discuss with the recruit</p>

              {/* Important Warning */}
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-red-800">Important Reminder</p>
                    <p className="text-xs text-red-700 mt-1">
                      Do not forward company emails to personal email, download customer lists, or transfer company data. Current employer may audit or scrub devices.
                    </p>
                  </div>
                </div>
              </div>

              {/* Business Setup */}
              <div className="bg-blue-50 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <BuildingOfficeIcon className="h-4 w-4 text-blue-600" />
                  <p className="text-sm font-semibold text-blue-800">Business Setup (Required)</p>
                </div>
                <div className="text-xs text-blue-700 space-y-2">
                  <div>
                    <p className="font-medium">Entity:</p>
                    <ul className="list-disc list-inside ml-1">
                      <li>S Corp or LLC</li>
                      <li><strong>EIN must be 14+ days old for A2P</strong></li>
                      <li>Use LegalZoom for faster setup</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium">Business Banking:</p>
                    <ul className="list-disc list-inside ml-1">
                      <li>Business bank account (required)</li>
                      <li>Business credit card (recommended)</li>
                      <li>Chase Ink Business Credit Card</li>
                    </ul>
                  </div>
                  <div className="text-blue-600 pt-1 border-t border-blue-200">
                    <p className="font-medium">Kim Ngo - Chase Bank</p>
                    <p>949-838-1468 | kim.ngo@chase.com</p>
                  </div>
                </div>
              </div>

              {/* State Licenses */}
              <div className="bg-slate-50 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <DocumentTextIcon className="h-4 w-4 text-lumin-primary" />
                  <p className="text-sm font-semibold text-slate-800">State Licensing</p>
                </div>
                <div className="text-xs text-slate-600 space-y-2">
                  <div>
                    <p className="font-medium">West Coast - 12 States (Recommended):</p>
                    <p>CA, CO, OR, WA, AZ, NM, TX</p>
                    <p className="text-slate-500">Optional: UT, ID, WY, NV* (*W2)</p>
                  </div>
                  <div>
                    <p className="font-medium">East Coast - 7 States:</p>
                    <p>OH, TN, MI, FL* (pending)</p>
                    <p className="text-slate-500">Optional: AL, KS, IL</p>
                  </div>
                  <div>
                    <p className="font-medium">In Flight - 3 States:</p>
                    <p>MD, PA, VA</p>
                  </div>
                </div>
              </div>

              {/* CA DRE */}
              <div className="bg-slate-50 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <BuildingOfficeIcon className="h-4 w-4 text-lumin-primary" />
                  <p className="text-sm font-semibold text-slate-800">California DRE (If Originating in CA)</p>
                </div>
                <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
                  <li>Fast Track: ~60 days</li>
                  <li>Self-Paced: 3-4 months</li>
                  <li>Testing: Must be in person in CA</li>
                </ul>
              </div>

              {/* Fees */}
              <div className="bg-amber-50 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCardIcon className="h-4 w-4 text-amber-600" />
                  <p className="text-sm font-semibold text-amber-800">Key Financial Expectations</p>
                </div>
                <ul className="text-xs text-amber-700 space-y-1">
                  <li><strong>Monthly Tech Fee:</strong> $300</li>
                  <li><strong>One-Time Setup Fee:</strong> $300 + $20-$30 (A2P + Phone)</li>
                  <li><strong>CRM Usage:</strong> ~$50-$150+/mo</li>
                  <li><strong>Commission Payouts:</strong> Fridays</li>
                </ul>
              </div>

              {/* Equipment */}
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ComputerDesktopIcon className="h-4 w-4 text-slate-600" />
                  <p className="text-sm font-semibold text-slate-800">Equipment Requirements</p>
                </div>
                <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
                  <li><strong>Laptop:</strong> High memory & performance (don't skimp)</li>
                  <li><strong>Bring Your Own:</strong> Monitors, keyboard, mouse</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminShell>
  );
};

export default RecruitForm;
